import DescriptionIcon from '@mui/icons-material/Description'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select, { type SelectChangeEvent } from '@mui/material/Select'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useEffect, useMemo, useState } from 'react'
import { useEmbedParams } from './lib/embed-params'
import logo from './assets/logo.svg'
import logoSmall from './assets/logoSmall.svg'

interface EpdRecord {
  id: string
  /** Facility `name` from API */
  plantName: string
  /** Product variant `name` from API */
  variantName: string
  downloadUrl: string | null
}

/** Matches published EPDs API: facility row */
interface ApiFacility {
  id?: number
  uuid?: string
  name?: string
  product_variants?: ApiProductVariant[]
}

/** Matches published EPDs API: nested product variant */
interface ApiProductVariant {
  id?: number
  uuid?: string
  name?: string
  epd_url?: string | null
}

/** Top-level published EPDs API response */
interface ApiPublishedEpdsResponse {
  name?: string
  facilities?: ApiFacility[]
}

function parsePublishedEpdsResponse(payload: unknown): {
  name: string | null
  facilities: ApiFacility[]
} {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    const body = payload as ApiPublishedEpdsResponse
    const trimmed = typeof body.name === 'string' ? body.name.trim() : ''
    const name = trimmed || null
    if (Array.isArray(body.facilities)) {
      return { name, facilities: body.facilities }
    }
    return { name, facilities: [] }
  }
  return { name: null, facilities: [] }
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL
const PUBLIC_EPDS_BEARER_TOKEN =
  import.meta.env.VITE_PUBLIC_PUBLISHED_EPDS_BEARER_TOKEN

function buildPublishedEpdsUrl(baseUrl: string): URL {
  const normalizedBase = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`
  return new URL('public/epds/published', normalizedBase)
}

function normalizeFacilityVariantRecord(
  facility: ApiFacility,
  variant: ApiProductVariant,
  index: number,
): EpdRecord {
  const downloadUrl = variant.epd_url

  return {
    id: String(
      variant.id ??
        variant.uuid ??
        `${facility.id ?? facility.uuid ?? facility.name ?? 'facility'}-${index}`,
    ),
    plantName: String(facility.name ?? ''),
    variantName: String(variant.name ?? ''),
    downloadUrl: downloadUrl ? String(downloadUrl) : null,
  }
}

function normalizeFacilitiesToRecords(facilities: ApiFacility[]): EpdRecord[] {
  return facilities.flatMap((facility, facilityIndex) => {
    const variants = facility.product_variants

    if (!Array.isArray(variants) || variants.length === 0) return []

    return variants.map((variant, variantIndex) =>
      normalizeFacilityVariantRecord(facility, variant, facilityIndex * 1000 + variantIndex),
    )
  })
}

function getFacilityPlantName(facility: ApiFacility): string | null {
  return facility.name ?? null
}

function getFacilityVariants(facility: ApiFacility): ApiProductVariant[] {
  const variants = facility.product_variants
  return Array.isArray(variants) ? variants : []
}

function extractPlantsFromFacilities(facilities: ApiFacility[]): string[] {
  const plants = facilities
    .map((facility) => getFacilityPlantName(facility))
    .filter((name): name is string => Boolean(name))

  return [...new Set(plants)]
}

function extractVariantNamesFromFacilities(
  facilities: ApiFacility[],
  plantFilter: string,
): string[] {
  const names = facilities.flatMap((facility) => {
    const plantName = getFacilityPlantName(facility)

    if (plantFilter && plantName !== plantFilter) return []

    return getFacilityVariants(facility)
      .map((variant) => variant.name)
      .filter((n): n is string => Boolean(n))
  })

  return [...new Set(names)]
}

function extractVariantNamesFromResponsePayload(payload: unknown, plantFilter: string): string[] {
  const { facilities } = parsePublishedEpdsResponse(payload)
  return extractVariantNamesFromFacilities(facilities, plantFilter)
}

function App() {
  const { manufacturerUuid } = useEmbedParams()
  const [plantFilter, setPlantFilter] = useState('')
  const [variantFilter, setVariantFilter] = useState('')
  const [records, setRecords] = useState<EpdRecord[]>([])
  const [plantOptions, setPlantOptions] = useState<string[]>([])
  const [variantOptions, setVariantOptions] = useState<string[]>([])
  const [rawPayload, setRawPayload] = useState<unknown>(null)
  const [registryName, setRegistryName] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [fetchError, setFetchError] = useState<string | null>(null)

  useEffect(() => {
    if (!manufacturerUuid) {
      setRecords([])
      setPlantOptions([])
      setVariantOptions([])
      setRawPayload(null)
      setRegistryName(null)
      setFetchError(null)
      return
    }

    const controller = new AbortController()

    const loadRecords = async () => {
      setLoading(true)
      setFetchError(null)

      try {
        const url = buildPublishedEpdsUrl(API_BASE_URL)
        url.searchParams.set('manufacturer_uuid', manufacturerUuid)

        const response = await fetch(url.toString(), {
          headers: {
            Authorization: `Bearer ${PUBLIC_EPDS_BEARER_TOKEN}`,
            Accept: 'application/json',
          },
          signal: controller.signal,
        })

        if (!response.ok) {
          throw new Error(`Request failed (${response.status})`)
        }

        const payload = (await response.json()) as unknown
        const { name, facilities } = parsePublishedEpdsResponse(payload)
        const normalized = normalizeFacilitiesToRecords(facilities)
        const plants = extractPlantsFromFacilities(facilities)
        const variants = extractVariantNamesFromFacilities(facilities, '')
        setRecords(normalized)
        setPlantOptions(plants)
        setVariantOptions(variants)
        setRegistryName(name)
        setRawPayload(payload)
      } catch (error) {
        if (controller.signal.aborted) return
        const message = error instanceof Error ? error.message : 'Failed to load records'
        setFetchError(message)
        setRecords([])
        setPlantOptions([])
        setVariantOptions([])
        setRawPayload(null)
        setRegistryName(null)
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false)
        }
      }
    }

    void loadRecords()

    return () => controller.abort()
  }, [manufacturerUuid])

  const plants = useMemo(() => {
    if (plantOptions.length > 0) return plantOptions
    return [...new Set(records.map((r) => r.plantName))]
  }, [plantOptions, records])

  const variantNames = useMemo(() => {
    // When a plant is selected, never show other plants' mixes — even if this plant has zero product_variants.
    if (plantFilter) {
      if (rawPayload) {
        return extractVariantNamesFromResponsePayload(rawPayload, plantFilter)
      }
      return [...new Set(records.filter((r) => r.plantName === plantFilter).map((r) => r.variantName))]
    }

    if (rawPayload) {
      return extractVariantNamesFromResponsePayload(rawPayload, '')
    }

    if (variantOptions.length > 0) return variantOptions
    return [...new Set(records.map((r) => r.variantName))]
  }, [rawPayload, plantFilter, variantOptions, records])

  const filtered = useMemo(
    () =>
      records.filter(
        (r) =>
          (!plantFilter || r.plantName === plantFilter) &&
          (!variantFilter || r.variantName === variantFilter),
      ),
    [plantFilter, variantFilter, records],
  )

  const handlePlantChange = (e: SelectChangeEvent) => {
    setPlantFilter(e.target.value)
    setVariantFilter('')
  }
  const handleVariantChange = (e: SelectChangeEvent) => setVariantFilter(e.target.value)

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: '100vh', bgcolor: 'background.default' }}>
      <Paper
        elevation={0}
        sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Box
            component="img"
            src={logoSmall}
            alt="Company logo"
            sx={{ display: { xs: 'block', sm: 'none' }, height: 36, width: 'auto' }}
          />
          <Box
            component="img"
            src={logo}
            alt="Company logo"
            sx={{ display: { xs: 'none', sm: 'block' }, height: 40, width: 'auto' }}
          />

          <Typography variant="h5" fontWeight={700} sx={{ flex: 1 }}>
            {registryName ? `EPDs for ${registryName}` : 'EPDs'}
          </Typography>
        </Box>

        {!manufacturerUuid && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No <strong>manufacturer_uuid</strong> provided. Pass it via URL query
            string or postMessage.
          </Alert>
        )}
        {fetchError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load published EPDs: {fetchError}
          </Alert>
        )}

        <Box
          sx={{
            display: 'flex',
            gap: 2,
            mb: 3,
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              Filter by Plant
            </Typography>
            <Select
              size="small"
              fullWidth
              value={plantFilter}
              onChange={handlePlantChange}
              displayEmpty
              renderValue={(val) => (val ? String(val) : 'Select Plant')}
              sx={{ '& .MuiSelect-select': { color: plantFilter ? 'text.primary' : 'text.secondary' } }}
            >
              <MenuItem value="">
                <em>All Plants</em>
              </MenuItem>
              {plants.map((p) => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box sx={{ flex: 1, minWidth: 220 }}>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
              Filter by mix
            </Typography>
            <Select
              size="small"
              fullWidth
              value={variantFilter}
              onChange={handleVariantChange}
              displayEmpty
              renderValue={(val) => (val ? String(val) : 'Select mix')}
              sx={{ '& .MuiSelect-select': { color: variantFilter ? 'text.primary' : 'text.secondary' } }}
            >
              <MenuItem value="">
                <em>All mixes</em>
              </MenuItem>
              {variantNames.map((m) => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  bgcolor: 'grey.100',
                  '& th': { fontWeight: 700, color: 'text.primary' },
                }}
              >
                <TableCell>Plant Name</TableCell>
                <TableCell>Mix</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Loading published EPDs...
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>{row.plantName}</TableCell>
                  <TableCell>{row.variantName || '—'}</TableCell>
                  <TableCell align="right">
                    {row.downloadUrl ? (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<DescriptionIcon />}
                        href={row.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: 'none' }}
                      >
                        Download
                      </Button>
                    ) : (
                      <Tooltip
                        title="There is a problem locating this EPD"
                        placement="right"
                        arrow
                      >
                        <Box component="span" sx={{ display: 'inline-flex' }}>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<DescriptionIcon />}
                            disabled
                            sx={{ textTransform: 'none' }}
                          >
                            Download
                          </Button>
                        </Box>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No records match the selected filters.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  )
}

export default App
